/** Card hỏi append nội dung chat lên form BSC — chỉ UI local + tag AI. */
import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Select, Space, message } from 'antd';
import {
  appendBscTextsToPage,
  bscApplyTargetsForStep,
  type BscApplyOfferState,
  type BscApplySection,
  type BscApplyTarget,
} from './bscApplyOffer';

export type { BscApplyOfferState };

interface Props {
  offer: BscApplyOfferState;
  pageStep?: string;
  onChange: (next: BscApplyOfferState) => void;
}

export const BscApplyOfferCard: React.FC<Props> = ({ offer, pageStep, onChange }) => {
  const [selected, setSelected] = useState<string[]>(() => offer.texts);
  const options = useMemo(() => bscApplyTargetsForStep(pageStep), [pageStep]);
  const target =
    options.find((t) => t.section === offer.section)
    ?? options[0]
    ?? ({ section: offer.section, label: offer.label, agents: [], step: pageStep ?? '' } as BscApplyTarget);

  if (offer.status === 'dismissed') return null;

  if (offer.status === 'applied') {
    return (
      <Alert
        type="success"
        showIcon
        style={{ marginTop: 12 }}
        message={offer.section === 'b6_target'
          ? `Đã điền ${selected.length || offer.texts.length} chỉ tiêu vào bảng — kiểm tra rồi bấm Lưu trên trang.`
          : `Đã thêm ${selected.length || offer.texts.length} mục vào «${offer.label}» — các dòng có nhãn AI trên form (chưa lưu — nhớ Lưu trang).`}
      />
    );
  }

  const apply = () => {
    const texts = selected.filter(Boolean);
    if (!texts.length) {
      message.warning('Chọn ít nhất một ý để thêm.');
      return;
    }
    onChange({ ...offer, status: 'applying' });
    try {
      // extra (aspects / items) song song với texts gốc — chỉ giữ khi người dùng thêm đủ mọi ý (B6: luôn gửi, lọc theo dòng chọn)
      const isB6 = offer.section === 'b6_target';
      appendBscTextsToPage(offer.section, texts, isB6 || texts.length === offer.texts.length ? offer.extra : undefined);
      onChange({ ...offer, status: 'applied', label: target.label });
      if (!isB6) message.success(`Đã thêm vào «${target.label}» — xem dòng có nhãn AI trên form`); // B6: trang tự báo số chỉ tiêu đã điền
    } catch (e) {
      const err = e instanceof Error ? e.message : 'Không thêm được nội dung.';
      onChange({ ...offer, status: 'error', error: err });
      message.error(err);
    }
  };

  const selectOptions = options.map((t) => ({ value: t.section, label: t.label }));

  return (
    <Card
      size="small"
      style={{ marginTop: 12 }}
      title="Thêm vào trang?"
      extra={
        selectOptions.length > 1 ? (
          <Select
            size="small"
            style={{ minWidth: 180 }}
            value={offer.section}
            options={selectOptions}
            disabled={offer.status === 'applying'}
            onChange={(section: BscApplySection) => {
              const t = options.find((x) => x.section === section);
              onChange({ ...offer, section, label: t?.label ?? offer.label });
            }}
            aria-label="Chọn phần trên trang"
          />
        ) : null
      }
    >
      <div style={{ marginBottom: 8, color: 'var(--ai-secondary)', fontSize: 13 }}>
        {offer.section === 'b6_target'
          ? 'Điền chỉ tiêu dưới đây cho KPI cùng tên trong bảng Đo lường chỉ số (chưa lưu — bạn bấm Lưu trên trang)?'
          : `Thêm các ý dưới đây vào «${target.label}» trên form (chưa gọi API lưu)?`}
      </div>
      <Checkbox.Group
        style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}
        value={selected}
        onChange={(vals) => setSelected(vals as string[])}
        options={offer.texts.map((text) => ({ label: text, value: text }))}
        disabled={offer.status === 'applying'}
      />
      {offer.status === 'error' && offer.error ? (
        <Alert type="error" showIcon style={{ marginTop: 8 }} message={offer.error} />
      ) : null}
      <Space style={{ marginTop: 12 }}>
        <Button type="primary" loading={offer.status === 'applying'} onClick={apply}>
          {offer.section === 'b6_target' ? 'Điền vào bảng' : 'Thêm vào trang'}
        </Button>
        <Button disabled={offer.status === 'applying'} onClick={() => onChange({ ...offer, status: 'dismissed' })}>
          Bỏ qua
        </Button>
      </Space>
    </Card>
  );
};

export default BscApplyOfferCard;

/** @deprecated dùng BscApplyOfferCard */
export const B1ApplyOfferCard = BscApplyOfferCard;
